import React from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../../i18n/useT';
import './HeroSection.css';

export default function HeroSection() {
  const t = useT();
  const h = t.hero;

  return (
    <section className="hero" aria-label="Hero section">
      <div className="hero__bg" aria-hidden="true">
        <div className="hero__bg-blob hero__bg-blob--1" />
        <div className="hero__bg-blob hero__bg-blob--2" />
        <div className="hero__bg-grid" />
      </div>

      <div className="container hero__container">
        <div className="hero__content">
          <div className="badge animate-fade-in-up" style={{ animationDelay: '0ms' }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
              <path d="M6 0L7.35 4.65L12 6L7.35 7.35L6 12L4.65 7.35L0 6L4.65 4.65L6 0Z"/>
            </svg>
            {h.badge}
          </div>

          <h1 className="hero__headline animate-fade-in-up" style={{ animationDelay: '80ms' }}>
            {h.headline1}
            <br />
            <span className="gradient-text">{h.headline2}</span>
          </h1>

          <p className="hero__sub animate-fade-in-up" style={{ animationDelay: '160ms' }}>
            {h.sub}
          </p>

          <div className="hero__actions animate-fade-in-up" style={{ animationDelay: '240ms' }}>
            <Link to="/contact" className="btn btn-primary btn-lg">
              {h.ctaPrimary}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <Link to="/features" className="btn btn-secondary btn-lg">
              {h.ctaSecondary}
            </Link>
          </div>

          <div className="hero__proof animate-fade-in-up" style={{ animationDelay: '320ms' }}>
            <div className="hero__proof-avatars" aria-hidden="true">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="hero__proof-avatar" style={{ background: `hsl(${i * 40}, 60%, 70%)` }} />
              ))}
            </div>
            <span>{h.socialProof}</span>
          </div>
        </div>

        <div className="hero__visual animate-float" aria-hidden="true">
          <div className="hero__mockup">
            <div className="hero__mockup-phone">
              <div className="hero__mockup-phone-header">
                <div className="hero__mockup-phone-dot" />
                <div className="hero__mockup-phone-dot" />
                <div className="hero__mockup-phone-dot" />
              </div>
              <div className="hero__mockup-phone-screen">
                <div className="hero__mockup-screen-top">
                  <div className="hero__mockup-topbar">
                    <span className="hero__mockup-label">My Documents</span>
                    <div className="hero__mockup-icon-row">
                      <div className="hero__mockup-icon-placeholder" />
                      <div className="hero__mockup-icon-placeholder" />
                    </div>
                  </div>
                  <div className="hero__mockup-search">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <circle cx="6" cy="6" r="4" stroke="#94A3B8" strokeWidth="1.5"/>
                      <path d="M10 10l2 2" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    <span>Search documents...</span>
                  </div>
                </div>
                <div className="hero__mockup-files">
                  {[
                    { name: 'Invoice Q3 2024', type: 'PDF', color: '#EFF6FF', icon: '#2563EB' },
                    { name: 'Tax Return 2023', type: 'PDF', color: '#F0FDF4', icon: '#10B981' },
                    { name: 'Contract – Acme Inc', type: 'PDF', color: '#FFF7ED', icon: '#F59E0B' },
                    { name: 'ID Document', type: 'IMG', color: '#FDF4FF', icon: '#A855F7' },
                  ].map((file, i) => (
                    <div key={i} className="hero__mockup-file" style={{ background: file.color }}>
                      <div className="hero__mockup-file-icon" style={{ background: file.icon + '20', color: file.icon }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M4 2h5l3 3v9H4V2z" fill="currentColor" opacity="0.2"/>
                          <path d="M4 2h5l3 3v9H4V2z" stroke="currentColor" strokeWidth="1.2"/>
                          <path d="M9 2v3h3" stroke="currentColor" strokeWidth="1.2"/>
                          <path d="M6 8h4M6 10h3" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <div className="hero__mockup-file-info">
                        <span className="hero__mockup-file-name">{file.name}</span>
                        <span className="hero__mockup-file-type">{file.type}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="hero__mockup-fab">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 4v12M4 10h12" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
              </div>
            </div>
            <div className="hero__mockup-badge hero__mockup-badge--scan">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 5V3h2M14 5V3h-2M2 11v2h2M14 11v2h-2" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round"/>
                <rect x="5" y="5" width="6" height="6" rx="1" stroke="#2563EB" strokeWidth="1.5"/>
              </svg>
              <span>{h.badge1}</span>
            </div>
            <div className="hero__mockup-badge hero__mockup-badge--secure">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 2L3 4v4c0 3 2.5 5 5 6 2.5-1 5-3 5-6V4L8 2z" stroke="#10B981" strokeWidth="1.5" strokeLinejoin="round"/>
                <path d="M5.5 8l2 2 3-3" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>{h.badge2}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
